import React, { FC, ReactNode, useState } from 'react'
import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'
import { Typography } from '../ui/Typography'
import { ButtonRegular } from '../ui/Button'
import { colors } from '../styles/colors'
import { DialogCloseButton } from '../components/DialogCloseButton'
import { useNavigate } from 'react-router'
import { BoxCreatedSuccessDialog } from './BoxCreatedSuccessDialog'
import { ActionRejectedDialog } from './ActionRejectedDialog'
import { profileMyBoxesPagePath } from '../pages/ProfilePage'

const DetailsItemComponent = ({
  label,
  value,
}: {
  label: ReactNode
  value: ReactNode
}) => {
  return (
    <DetailsItem>
      <dt>
        <Typography
          fontSize={14}
          fontWeight={400}
          color={colors.grey700}
          font="roboto"
        >
          {label}
        </Typography>
      </dt>

      <dd>
        <Typography
          fontSize={14}
          fontWeight={400}
          color={colors.grey700}
          font="roboto"
        >
          {value}
        </Typography>
      </dd>
    </DetailsItem>
  )
}

export const CreateBoxConfirmDialog: FC<{
  onClose: () => void
  onConfirm: () => Promise<void>
  data: {
    commission: string
    earnings: string
  }
}> = ({ onClose, onConfirm, data }) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState<null | true>(null)
  const [isError, setIsError] = useState(false)

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await onConfirm?.()
      setIsError(false)
      setIsSuccess(true)
    } catch (error) {
      console.error(error)
      setIsError(true)
      setIsSuccess(null)
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <BoxCreatedSuccessDialog
        onClose={onClose}
        onViewMyBoxes={() => navigate(profileMyBoxesPagePath)}
        onShareBox={() => navigate(profileMyBoxesPagePath)}
      />
    )
  }

  if (isError) {
    return (
      <ActionRejectedDialog
        onClose={onClose}
        isLoading={isLoading}
        onRetry={handleConfirm}
      />
    )
  }

  return (
    <DialogContainer>
      {onClose && <DialogCloseButton onClick={onClose} />}

      <TitleContainer>
        <Typography fontSize={20} fontWeight={600} color={colors.secondary}>
          <FormattedMessage
            id="dialog.createBoxConfirm.title"
            defaultMessage="Подтвердите создание нового бокса"
          />
        </Typography>
      </TitleContainer>

      <DetailsContainer>
        {[
          {
            id: 'commission',
            key: (
              <FormattedMessage
                id="dialog.createBoxConfirm.commission"
                defaultMessage="Комиссия"
              />
            ),
            value: data.commission,
          },

          {
            id: 'earnings',
            key: (
              <FormattedMessage
                id="dialog.createBoxConfirm.earnings"
                defaultMessage="Ваш заработок с бокса"
              />
            ),
            value: data.earnings,
          },
        ].map(({ key, value, id }) => (
          <DetailsItemComponent key={id} label={key} value={value} />
        ))}
      </DetailsContainer>

      <ButtonsContainer>
        <ConfirmButton
          loading={isLoading}
          onClick={handleConfirm}
          textColor={colors.white}
          textWeight={700}
        >
          <FormattedMessage
            id="dialog.createBoxConfirm.confirm"
            defaultMessage="Подтвердить"
          />
        </ConfirmButton>

        <CancelButton onClick={onClose}>
          <FormattedMessage
            id="dialog.createBoxConfirm.cancel"
            defaultMessage="Отмена"
          />
        </CancelButton>
      </ButtonsContainer>
    </DialogContainer>
  )
}

export const createBoxConfirmDialogPath = '/create-box-confirm-dialog'

const DialogContainer = styled.div`
  position: relative;
  display: grid;
  place-items: center;
  gap: 32px;
  background: ${colors.bgPrimary};
  border-radius: 20px;
  padding: 32px 24px;
  width: 100%;
  max-width: 400px;
`

const TitleContainer = styled.div`
  text-align: center;
  max-width: 280px;
`

const DetailsContainer = styled.dl`
  width: 100%;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 16px;
`

const DetailsItem = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;

  dt {
    margin: 0;
  }

  dd {
    margin: 0;
  }
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: grid;
  gap: 12px;
  margin-top: 32px;
`

const ConfirmButton = styled(ButtonRegular)`
  width: 100%;
  background: ${colors.secondary};
  color: ${colors.white};
`

const CancelButton = styled(ButtonRegular)`
  width: 100%;
  background: transparent;
  border: 1px solid ${colors.secondary};
  color: ${colors.textPrimary};
`
